/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/shared/components/ui/use-toast";
import { get, del } from "@/shared/utils/fetchApi";
import { ColumnState } from "@/shared/components/ui/category-filter";

export interface Embedding {
  id: string;
  inserted_at: string;
  version: number;
  metadata: Record<string, unknown>;
}

export interface EmbeddingDetails extends Embedding {
  content: string;
  text_search: Record<string, unknown>;
  embedding: Record<string, unknown>;
  knowledge_id: string;
}

export interface EmbeddingsResponse {
  data: Embedding[];
  count: number;
}

export const useEmbeddings = (
  knowledgeBaseId: string | undefined,
  searchTerm?: string | null,
  selectedColumns?: ColumnState,
) => {
  const [embeddings, setEmbeddings] = useState<Embedding[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { t } = useTranslation();
  const { toast } = useToast();

  const fetchEmbeddings = async (page: number, pageSize: number) => {
    // Sem knowledgeBaseId não há o que buscar — sai sem chamar a api.
    if (!knowledgeBaseId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const basePath = "/embeddings";
      let url = `${basePath}?knowledge_base_id=${knowledgeBaseId}&page=${page}&pageSize=${pageSize}`;

      if (searchTerm && searchTerm.length > 0) {
        const isDateLike = /^\d{4}-\d{2}-\d{2}$/.test(searchTerm);

        if (isDateLike) {
          url += `&isDateSearch=true`;
          url += `&startDate=${searchTerm}`;
        } else {
          url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
        }
      }

      if (selectedColumns) {
        const activeColumns = Object.entries(selectedColumns)
          .filter(([_, isActive]) => isActive)
          .map(([columnId]) => columnId);

        if (activeColumns.length > 0) {
          url += `&columns=${activeColumns.join(",")}`;
        }
      }

      const response = await get<{
        data: Embedding[];
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
      }>(url);


      setEmbeddings(response.data || []);
      setTotalCount((response as { total: number }).total || 0);

      // Se não houver resultados e estiver filtrando por data, exibir uma mensagem
      if (
        response.data.length === 0 &&
        searchTerm &&
        /^\d{4}-\d{2}-\d{2}$/.test(searchTerm)
      ) {
        toast({
          title: t("knowledge.embeddings.noResults"),
          description: t("knowledge.embeddings.tryAnotherDate"),
          variant: "default",
        });
      }
    } catch (err: any) {
      console.error("[useEmbeddings] Error fetching embeddings:", err);
      const errorMessage = err.message || String(err);
      setError(errorMessage);
      toast({
        title: t("common.error"),
        description: t("knowledge.embeddings.fetchError"),
        variant: "destructive",
      });

      // Limpar os dados em caso de erro
      setEmbeddings([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmbeddingDetails = async (
    id: string,
  ): Promise<EmbeddingDetails | null> => {
    try {
      const response = await get<EmbeddingDetails>(`/embeddings/${id}`);
      return response;
    } catch (err: any) {
      console.error("[useEmbeddings] Error fetching embedding details:", err);
      toast({
        title: t("common.error"),
        description: t("knowledge.embeddings.fetchDetailError"),
        variant: "destructive",
      });
      return null;
    }
  };

  const deleteEmbedding = async (id: string): Promise<boolean> => {
    try {
      await del(`/embeddings/${id}`, { parseJson: false });

      toast({
        title: t("common.success"),
        description: t("knowledge.embeddings.deleteSuccess"),
      });

      fetchEmbeddings(page, pageSize);
      return true;
    } catch (err: any) {
      console.error("[useEmbeddings] Error deleting embedding:", err);
      toast({
        title: t("common.error"),
        description: t("knowledge.embeddings.deleteError"),
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    if (knowledgeBaseId) {
      if (searchTerm) {
        setPage(1);
        setEmbeddings([]);
      }

      fetchEmbeddings(searchTerm ? 1 : page, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeBaseId, pageSize, searchTerm, selectedColumns]);

  // Separate effect for page changes to avoid conflicts with search term changes
  useEffect(() => {
    if (knowledgeBaseId && !searchTerm) {
      fetchEmbeddings(page, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, knowledgeBaseId]);

  return {
    embeddings,
    totalCount,
    loading,
    error,
    page,
    setPage,
    pageSize,
    setPageSize,
    fetchEmbeddingDetails,
    deleteEmbedding,
    refetch: () => fetchEmbeddings(page, pageSize),
  };
};
